import {
  ChevronLeftIcon,
  SettingsIcon,
  SidebarShowIcon,
} from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { cn } from "cn";
import { useState } from "react";

import { analytics } from "../lib/telemetry/analytics.ts";

import { UNTITLED_CONVERSATION } from "../chat/conversations.ts";
import { ConversationUsage } from "../usage/ConversationUsage.tsx";
import { useWorkspace } from "./AppShell.tsx";
import { useSession } from "./session.tsx";
import { SettingsDialog } from "./SettingsDialog.tsx";
import { Sidebar, type SidebarPanel } from "./Sidebar.tsx";
import { TopBar } from "./TopBar.tsx";
import { pathOf } from "./workspace.ts";

/**
 * El armazón conversacional (§9.2).
 *
 * Es el ÚNICO layout con historial y redactor: la columna de la izquierda con
 * los chats y la memoria --y dentro de la memoria, las mecánicas (§7.3)--, y
 * al centro lo que se esté leyendo, que es la conversación o el documento de
 * una mecánica.
 *
 * Ocupa la ventana entera y NO centra nada: la barra lateral se pega al borde
 * izquierdo y el contenido se queda con el resto.
 */
export function ChatShell() {
  const { chat, onConversationDeleted } = useWorkspace();
  const { canSeeCosts } = useSession();
  const navigate = useNavigate();
  const [sidebarHidden, setSidebarHidden] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const readingMechanic = pathname.startsWith("/memoria/mecanicas");

  /**
   * El panel elegido, con un valor por defecto que depende de dónde estés.
   *
   * Sin elección propia, leer una mecánica enseña la memoria --que es de donde
   * salió, y donde está la lista para saltar a otra-- y todo lo demás enseña
   * los chats. En cuanto el usuario elige, manda su elección: abrir el
   * historial mientras se lee un documento es legítimo.
   */
  const [chosenPanel, setChosenPanel] = useState<SidebarPanel | null>(null);
  const panel = chosenPanel ?? (readingMechanic ? "memoria" : "chats");

  const goToChat = () => {
    void navigate({ to: "/", search: (previous) => previous });
  };

  return (
    <div className="flex h-dvh overflow-hidden">
      {!sidebarHidden && (
        <Sidebar
          currentId={chat.conversationId}
          panel={panel}
          onPanel={(next) => {
            analytics.nav.sidebarPanelChanged({ panel: next });
            setChosenPanel(next);
          }}
          onOpen={(id) => {
            chat.open(id);
            goToChat();
          }}
          onDeleted={onConversationDeleted}
          // "Conversación nueva" vuelve a significar solo eso: empezar de
          // cero. Ya no es la única forma de ver el conmutador de modos.
          onNew={() => {
            analytics.chat.conversationNew({ origin: "sidebar" });
            chat.startNew();
            goToChat();
          }}
          onHide={() => {
            analytics.nav.sidebarToggled({ visible: false });
            setSidebarHidden(true);
          }}
        />
      )}

      {/* `min-w-0` y `min-h-0`: un hijo flex vale por defecto "no me
          encojas por debajo de mi contenido", y sin esto una tabla ancha
          estira el armazón y la página entera se mueve en horizontal. */}
      <main className="flex min-h-0 min-w-0 flex-1 flex-col">
        <TopBar
          mode="chat"
          onMode={(next) => {
            void navigate({
              to: pathOf(next),
              search: (previous) => previous,
            });
          }}
          context={
            <>
              {sidebarHidden && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    analytics.nav.sidebarToggled({ visible: true });
                    setSidebarHidden(false);
                  }}
                  aria-label="Mostrar el panel"
                >
                  <SidebarShowIcon />
                </Button>
              )}

              {readingMechanic ? (
                <Button variant="ghost" size="sm" onClick={goToChat}>
                  <ChevronLeftIcon /> Memoria
                </Button>
              ) : (
                chat.conversationId !== undefined && (
                  /* Mientras el backend le pone nombre, la cabecera lo dice
                     con las mismas palabras que el historial y en el mismo
                     sitio: solo cambia el color, así que cuando llegue el
                     título de verdad no salta nada. */
                  <h1
                    className={cn(
                      "truncate text-base font-semibold",
                      chat.title === null &&
                        "font-normal text-muted-foreground",
                    )}
                    title={chat.title ?? UNTITLED_CONVERSATION}
                  >
                    {chat.title ?? UNTITLED_CONVERSATION}
                  </h1>
                )
              )}

              {canSeeCosts && !readingMechanic && chat.usage !== undefined && (
                <ConversationUsage usage={chat.usage} />
              )}
            </>
          }
          actions={
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                analytics.nav.settingsOpened({ mode: "chat" });
                setSettingsOpen(true);
              }}
              aria-label="Ajustes"
            >
              <SettingsIcon />
            </Button>
          }
        />

        <Outlet />
      </main>

      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </div>
  );
}
